/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Typography2 } from '@coursera/cds-core';

const styles = {
  root: css`
    background-color: var(--cds-color-neutral-background-primary);
    border: 1px solid var(--cds-color-neutral-stroke-primary);
    border-radius: var(--cds-border-radius-100);
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    overflow: hidden;
    width: 100%;
    cursor: pointer;
    text-align: start;
    padding: 0;

    &:hover:not(:disabled) {
      border-color: var(--cds-color-neutral-stroke-primary);
    }

    &:active:not(:disabled) {
      border-color: var(--cds-color-interactive-primary-pressed);
      background-color: var(--cds-color-interactive-background-primary-pressed-weak);
    }

    &:focus-visible {
      outline: 2px solid var(--cds-color-interactive-stroke-primary-focus);
      outline-offset: 2px;
    }

    &:disabled {
      cursor: default;
    }
  `,

  inner: css`
    display: grid;
    grid-template-columns: auto 1fr;
    align-self: stretch;
    width: 100%;
  `,

  imageSlot: css`
    display: flex;
    align-items: center;
    justify-content: center;
    padding-left: var(--cds-spacing-100);
    padding-block: var(--cds-spacing-100);
    flex-shrink: 0;
  `,

  imageSlotCard: css`
    height: 100%;
    max-height: var(--cds-spacing-600);
    aspect-ratio: 1 / 1;
    background-color: var(--cds-color-emphasis-secondary-background-xweak);
    border-radius: var(--cds-border-radius-100);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  `,

  imageSlotDisabled: css`
    opacity: 0.5;
  `,

  imageContent: css`
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    & svg {
      width: 24px;
      height: 24px;
    }
  `,

  content: css`
    display: flex;
    flex: 1 0 0;
    flex-direction: column;
    gap: var(--cds-spacing-50);
    align-items: flex-start;
    align-self: stretch;
    justify-content: center;
    min-width: 0;
    padding-inline: var(--cds-spacing-150);
    padding-block: var(--cds-spacing-200);
  `,

  titleDisabled: css`
    color: var(--cds-color-neutral-disabled-strong);
  `,

  description: css`
    color: var(--cds-color-neutral-primary-weak);
  `,

  descriptionDisabled: css`
    color: var(--cds-color-neutral-disabled);
  `,
};

export type SuggestionItem = {
  /** Stable unique identifier for the item, used as React key. */
  id: string;
  /** Primary label displayed in the card. */
  title: string;
  /** Optional supporting text displayed below the title. */
  description?: string;
  /** Visual element rendered in the left image slot (should include aria-hidden or alt text). */
  image: React.ReactNode;
  /** Called when the suggestion card is clicked. */
  onClick?: () => void;
  /** Prevents interaction and applies disabled styling. */
  disabled?: boolean;
};

type FeaturedSuggestionProps = {
  suggestion: SuggestionItem;
};

const FeaturedSuggestion = ({ suggestion }: FeaturedSuggestionProps) => {
  const { title, description, image, onClick, disabled } = suggestion;

  return (
    <button
      type="button"
      css={styles.root}
      onClick={onClick}
      disabled={disabled}
      aria-label={title}
      data-testid="FeaturedSuggestion"
    >
      <div css={styles.inner}>
        <div css={[styles.imageSlot, disabled && styles.imageSlotDisabled]}>
          <div css={styles.imageSlotCard}>
            <div css={styles.imageContent}>{image}</div>
          </div>
        </div>
        <div css={styles.content}>
          <Typography2 variant="subtitleMedium" component="span" css={disabled ? styles.titleDisabled : undefined}>
            {title}
          </Typography2>
          {description && (
            <Typography2
              variant="bodySecondary"
              component="span"
              css={disabled ? styles.descriptionDisabled : styles.description}
            >
              {description}
            </Typography2>
          )}
        </div>
      </div>
    </button>
  );
};

export default FeaturedSuggestion;
